import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import type { ReactNode } from 'react'
import { createClient } from '@connectrpc/connect'
import { useStore } from '@tanstack/react-store'
import { create } from '@bufbuild/protobuf'
import { useQueryClient } from '@tanstack/react-query'
import { createConnectQueryKey } from '@connectrpc/connect-query'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'

import { connectTransport } from '@/lib/connect-transport'
import { organizationStore } from '@/stores/organization.store'
import { useHasAIAgentFeature } from '@/hooks/use-has-feature'
import { timestampToDate } from '@/lib/inquiry-utils'
import {
  AgentService,
  AgentSendMessageRequestSchema,
  AgentRouteContextSchema,
  AgentExecuteActionRequestSchema,
  AgentRejectActionRequestSchema,
  AgentSubmitQuestionnaireRequestSchema,
  AgentQuestionnaireAnswerSchema,
  ListAgentSessionsRequestSchema,
  GetAgentSessionRequestSchema,
  GetAgentTurnRequestSchema,
  AgentTurnDisclosureSchema,
  AgentResumeTurnRequestSchema,
  ListAgentApprovalsRequestSchema,
  ListAgentCapabilitiesRequestSchema,
  AgentEventType,
  AgentActionStatusEnum,
  AgentAttentionKindEnum,
  AgentQuestionnaireStatusEnum,
  type AgentAttentionItem,
  type PendingAction,
  type AgentSessionLite,
  type AgentQuestionnaire,
  type AgentQuestionnaireAnswer,
  type AgentStreamEvent,
  type AgentCapability,
  type AgentTurnDisclosure,
} from '@/gen/cortado/v1/agent_pb'
import { useRouteContext } from '@/hooks/use-route-context'
import {
  getBooking,
  getDashboardSummary,
  getEvent,
  getInquiry,
  getInvoice,
  getQuote,
  getTask,
  listEvents,
  listInquiries,
  listOrganizationInquiries,
  listTasks,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'

export interface ToolExecution {
  toolName: string
  toolId: string
  status: 'running' | 'completed'
  resultJson?: string
}

/**
 * One element of a turn's ordered content — text and tool results interleaved
 * in the position they actually occurred, mirroring `AgentMessagePart` on the
 * wire, so the transcript can render a tool widget inline between two prose
 * segments instead of hoisting every tool call above all the text.
 */
export type MessagePart =
  | { type: 'text'; text: string }
  | { type: 'tool'; tool: ToolExecution }

export interface Message {
  id: string
  role: 'user' | 'assistant'
  parts: MessagePart[]
  isStreaming?: boolean
  /** Durable turn id for decision disclosure replay. */
  turnId?: string
  /** Ids of PendingAction proposals surfaced during this turn — look up current state in `pendingActions`. */
  proposalIds?: string[]
  /** Ids of structured user-input requests rendered in this assistant turn. */
  questionnaireIds?: string[]
}

export interface AgentSessionSummary {
  id: string
  overview: string
  channel: string
  messageCount: number
  createdAt: Date
  updatedAt: Date
}

interface AIAssistantContextType {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
  messages: Message[]
  sessionId: string | null
  isProcessing: boolean
  sendMessage: (content: string) => Promise<void>
  clearMessages: () => void
  startNewSession: () => void
  sessions: AgentSessionSummary[]
  isLoadingSessions: boolean
  fetchSessions: () => Promise<void>
  loadSession: (sessionId: string) => Promise<void>
  isOnChatPage: boolean
  setIsOnChatPage: (value: boolean) => void
  pendingActions: Record<string, PendingAction>
  approvals: PendingAction[]
  attentionItems: AgentAttentionItem[]
  attentionTotalCount: number
  capabilities: AgentCapability[]
  capabilityContractVersion: string
  isLoadingFoundation: boolean
  fetchAgentFoundation: () => Promise<void>
  approveAction: (proposalId: string) => Promise<void>
  rejectAction: (proposalId: string) => Promise<void>
  autopilotMode: boolean
  toggleAutopilot: () => void
  questionnaires: Record<string, AgentQuestionnaire>
  submitQuestionnaire: (questionnaireId: string, answers: QuestionnaireAnswerInput[]) => Promise<void>
  isWaitingForInput: boolean
  hasUnreadResponse: boolean
  disclosures: Record<string, AgentTurnDisclosure>
  fetchDisclosure: (turnId: string) => Promise<void>
  isLoadingDisclosure: (turnId: string) => boolean
}

export interface QuestionnaireAnswerInput {
  questionId: string
  values: string[]
}

interface AgentStreamResult {
  succeeded: boolean
  aborted: boolean
}

const AIAssistantContext = createContext<AIAssistantContextType | undefined>(undefined)

let localMessageSequence = 0

function createLocalMessageId(role: Message['role']) {
  localMessageSequence += 1
  return `${role}-${Date.now()}-${localMessageSequence}`
}

// Create the client for AgentService
const agentClient = createClient(AgentService, connectTransport)

function formatQuestionnaireResponse(questionnaire: AgentQuestionnaire, answers: QuestionnaireAnswerInput[]) {
  const answersByQuestion = new Map(answers.map((answer) => [answer.questionId, answer.values]))
  const lines = questionnaire.questions.flatMap((question) => {
    const value = answersByQuestion.get(question.id)?.[0]
    if (!value) return []
    const displayValue = question.choices.find((choice) => choice.value === value)?.label ?? value
    return [`- ${question.prompt}: ${displayValue}`]
  })
  return [`Responses to “${questionnaire.title}”`, ...lines].join('\n')
}

async function* resumableAgentStream(
  organizationId: string,
  requestId: string,
  signal: AbortSignal,
  initial: () => AsyncIterable<AgentStreamEvent>,
): AsyncIterable<AgentStreamEvent> {
  let cursor = 0n
  let terminal = false
  try {
    for await (const event of initial()) {
      if (event.sequence > 0n) {
        if (event.sequence <= cursor) continue
        cursor = event.sequence
      }
      if (event.type === AgentEventType.COMPLETION || event.type === AgentEventType.ERROR) terminal = true
      yield event
    }
  } catch (error) {
    if (signal.aborted || (error instanceof Error && error.name === 'AbortError')) throw error
  }
  if (terminal || signal.aborted) return

  const request = create(AgentResumeTurnRequestSchema, {
    organizationId,
    lookup: { case: 'requestId', value: requestId },
    afterSequence: cursor,
  })
  for await (const wrapped of agentClient.resumeTurn(request, { signal })) {
    const event = wrapped.event
    if (!event) continue
    if (event.sequence > 0n) {
      if (event.sequence <= cursor) continue
      cursor = event.sequence
    }
    if (event.type === AgentEventType.COMPLETION || event.type === AgentEventType.ERROR) terminal = true
    yield event
  }
  if (!terminal && !signal.aborted) throw new Error('Agent turn ended before a terminal event')
}

export function AIAssistantProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [sessions, setSessions] = useState<AgentSessionSummary[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [isOnChatPage, setIsOnChatPage] = useState(false)
  const [pendingActions, setPendingActions] = useState<Record<string, PendingAction>>({})
  const [approvals, setApprovals] = useState<PendingAction[]>([])
  const [attentionItems, setAttentionItems] = useState<AgentAttentionItem[]>([])
  const [attentionTotalCount, setAttentionTotalCount] = useState(0)
  const [capabilities, setCapabilities] = useState<AgentCapability[]>([])
  const [capabilityContractVersion, setCapabilityContractVersion] = useState('')
  const [isLoadingFoundation, setIsLoadingFoundation] = useState(false)
  const [questionnaires, setQuestionnaires] = useState<Record<string, AgentQuestionnaire>>({})
  const [autopilotMode, setAutopilotMode] = useState(false)
  const [hasUnreadResponse, setHasUnreadResponse] = useState(false)
  const [disclosures, setDisclosures] = useState<Record<string, AgentTurnDisclosure>>({})
  const [loadingDisclosureTurnIds, setLoadingDisclosureTurnIds] = useState<Set<string>>(() => new Set())
  const inFlightDisclosureTurnIdsRef = useRef<Set<string>>(new Set())
  const abortControllerRef = useRef<AbortController | null>(null)
  // Mirrors isOpen/isOnChatPage for the completion handler below, which needs the
  // value *at the moment the stream finishes* without retriggering runAgentStream's
  // identity on every panel open/close.
  const isOpenRef = useRef(isOpen)
  const isOnChatPageRef = useRef(isOnChatPage)

  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const { hasFeature: hasAIAgent } = useHasAIAgentFeature()
  const routeContext = useRouteContext()

  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)
  const toggle = () => setIsOpen((prev) => !prev)
  const toggleAutopilot = useCallback(() => setAutopilotMode((prev) => !prev), [])

  const clearMessages = useCallback(() => {
    setMessages([])
    setSessionId(null)
    setPendingActions({})
    setQuestionnaires({})
  }, [])

  const startNewSession = useCallback(() => {
    // Cancel any in-progress request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    clearMessages()
  }, [clearMessages])

  // Convert proto AgentSessionLite to AgentSessionSummary
  const convertSessionLite = useCallback((session: AgentSessionLite): AgentSessionSummary => {
    return {
      id: session.id,
      overview: session.overview || 'Untitled conversation',
      channel: session.channel,
      messageCount: session.messageCount,
      createdAt: timestampToDate(session.createdAt) ?? new Date(),
      updatedAt: timestampToDate(session.updatedAt) ?? new Date(),
    }
  }, [])

  const fetchSessions = useCallback(async () => {
    if (!selectedOrganizationId) return

    setIsLoadingSessions(true)
    try {
      const request = create(ListAgentSessionsRequestSchema, {
        organizationId: selectedOrganizationId,
      })

      const response = await agentClient.listAgentSessions(request)
      setSessions(response.sessions.map(convertSessionLite))
    } catch (error) {
      console.error('Failed to fetch agent sessions:', error)
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'ai-assistant-fetch-sessions' })
    } finally {
      setIsLoadingSessions(false)
    }
  }, [selectedOrganizationId, convertSessionLite])

  const fetchDisclosure = useCallback(async (turnId: string) => {
    if (!selectedOrganizationId || !turnId) return
    if (disclosures[turnId] !== undefined) return
    if (inFlightDisclosureTurnIdsRef.current.has(turnId)) return

    inFlightDisclosureTurnIdsRef.current.add(turnId)
    setLoadingDisclosureTurnIds((previous) => new Set(previous).add(turnId))
    try {
      const response = await agentClient.getAgentTurn(create(GetAgentTurnRequestSchema, {
        organizationId: selectedOrganizationId,
        turnId,
      }))
      setDisclosures((previous) => ({
        ...previous,
        [turnId]: response?.disclosure ?? create(AgentTurnDisclosureSchema, {}),
      }))
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'ai-assistant-fetch-disclosure' })
      setDisclosures((previous) => ({
        ...previous,
        [turnId]: create(AgentTurnDisclosureSchema, {}),
      }))
    } finally {
      inFlightDisclosureTurnIdsRef.current.delete(turnId)
      setLoadingDisclosureTurnIds((previous) => {
        const next = new Set(previous)
        next.delete(turnId)
        return next
      })
    }
  }, [selectedOrganizationId, disclosures])

  const isLoadingDisclosure = useCallback(
    (turnId: string) => loadingDisclosureTurnIds.has(turnId),
    [loadingDisclosureTurnIds],
  )

  const fetchAgentFoundation = useCallback(async () => {
    if (!selectedOrganizationId) return
    setIsLoadingFoundation(true)
    try {
      const [approvalResponse, capabilityResponse] = await Promise.all([
        agentClient.listAgentApprovals(create(ListAgentApprovalsRequestSchema, {
          organizationId: selectedOrganizationId,
          kinds: [
            AgentAttentionKindEnum.AGENT_ATTENTION_KIND_PROPOSAL,
            AgentAttentionKindEnum.AGENT_ATTENTION_KIND_QUESTIONNAIRE,
            AgentAttentionKindEnum.AGENT_ATTENTION_KIND_FAILED_EXECUTION,
            AgentAttentionKindEnum.AGENT_ATTENTION_KIND_STUCK_TURN,
          ],
        })),
        agentClient.listAgentCapabilities(create(ListAgentCapabilitiesRequestSchema, {
          organizationId: selectedOrganizationId,
        })),
      ])
      setApprovals(approvalResponse.approvals)
      setAttentionItems(approvalResponse.items)
      setAttentionTotalCount(approvalResponse.totalCount)
      setPendingActions((previous) => {
        const next = { ...previous }
        for (const approval of approvalResponse.approvals) next[approval.id] = approval
        return next
      })
      setCapabilities(capabilityResponse.capabilities)
      setCapabilityContractVersion(capabilityResponse.contractVersion)
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'ai-assistant-fetch-foundation' })
    } finally {
      setIsLoadingFoundation(false)
    }
  }, [selectedOrganizationId])

  const loadSession = useCallback(
    async (targetSessionId: string) => {
      if (!selectedOrganizationId) return

      try {
        const request = create(GetAgentSessionRequestSchema, {
          organizationId: selectedOrganizationId,
          sessionId: targetSessionId,
        })

        const response = await agentClient.getAgentSession(request)

        const proposalsById: Record<string, PendingAction> = {}
        for (const proposal of response.proposals) {
          proposalsById[proposal.id] = proposal
        }

        const questionnairesById: Record<string, AgentQuestionnaire> = {}
        for (const questionnaire of response.questionnaires) {
          questionnairesById[questionnaire.id] = questionnaire
        }

        const loadedMessages: Message[] = response.messages.map((m, index) => ({
          id: m.id || `${targetSessionId}-${index}`,
          role: m.role as 'user' | 'assistant',
          turnId: m.turnId,
          // Already in true chronological order and server-filtered to the
          // renderable tool set — no client-side fallback needed.
          parts: m.parts.flatMap((part): MessagePart[] => {
            if (part.part.case === 'text') return [{ type: 'text', text: part.part.value }]
            if (part.part.case === 'toolResult') {
              const result = part.part.value
              return [{
                type: 'tool',
                tool: { toolName: result.toolName, toolId: result.toolId, status: 'completed' as const, resultJson: result.resultJson },
              }]
            }
            return []
          }),
        }))

        const proposalsWithoutMessage: string[] = []
        for (const proposal of response.proposals) {
          const targetIndex = proposal.messageId
            ? loadedMessages.findIndex((message) => message.id === proposal.messageId)
            : -1
          if (targetIndex < 0) {
            proposalsWithoutMessage.push(proposal.id)
            continue
          }
          const message = loadedMessages[targetIndex]
          loadedMessages[targetIndex] = {
            ...message,
            proposalIds: [...(message.proposalIds ?? []), proposal.id],
          }
        }

        // Proposals written before message linkage was fixed have a NULL message
        // id. Keep those legacy approvals reachable under the final assistant
        // message without collapsing newly linked proposals there too.
        if (proposalsWithoutMessage.length > 0) {
          for (let i = loadedMessages.length - 1; i >= 0; i--) {
            if (loadedMessages[i].role === 'assistant') {
              loadedMessages[i] = {
                ...loadedMessages[i],
                proposalIds: [...(loadedMessages[i].proposalIds ?? []), ...proposalsWithoutMessage],
              }
              break
            }
          }
        }

        for (const questionnaire of response.questionnaires) {
          let targetIndex = questionnaire.messageId
            ? loadedMessages.findIndex((message) => message.id === questionnaire.messageId)
            : -1
          if (targetIndex < 0) {
            for (let i = loadedMessages.length - 1; i >= 0; i--) {
              if (loadedMessages[i].role === 'assistant') {
                targetIndex = i
                break
              }
            }
          }
          if (targetIndex >= 0) {
            const message = loadedMessages[targetIndex]
            loadedMessages[targetIndex] = {
              ...message,
              questionnaireIds: [...(message.questionnaireIds ?? []), questionnaire.id],
            }
          }
        }

        setMessages(loadedMessages)
        setPendingActions(proposalsById)
        setQuestionnaires(questionnairesById)
        setSessionId(response.session?.id ?? targetSessionId)
      } catch (error) {
        console.error('Failed to load agent session:', error)
        captureError(error instanceof Error ? error : new Error(String(error)), { context: 'ai-assistant-load-session' })
      }
    },
    [selectedOrganizationId]
  )

  const queryClient = useQueryClient()

  // Invalidate relevant query caches so entity lists reflect an executed action
  const invalidateForEntityType = useCallback(
    (entityType: string, actionType = '') => {
      if (entityType === 'task' || actionType === 'create_task' || actionType === 'update_task') {
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: listTasks, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getTask, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getDashboardSummary, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getInquiry, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getBooking, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getEvent, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getQuote, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getInvoice, cardinality: undefined }) })
        return
      }
      if (entityType === 'inquiry') {
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: listInquiries, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: listOrganizationInquiries, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getDashboardSummary, cardinality: undefined }) })
      } else if (entityType === 'event') {
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: listEvents, cardinality: undefined }) })
        queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getDashboardSummary, cardinality: undefined }) })
      }
    },
    [queryClient]
  )

  const approveAction = useCallback(
    async (proposalId: string) => {
      if (!selectedOrganizationId) return

      const request = create(AgentExecuteActionRequestSchema, {
        organizationId: selectedOrganizationId,
        proposalId,
      })

      const response = await agentClient.executeAction(request)
      const action = response.action

      if (action) {
        setPendingActions((prev) => ({ ...prev, [action.id]: action }))
        setApprovals((prev) => prev.filter((proposal) => proposal.id !== action.id))
      }

      if (action?.status === AgentActionStatusEnum.AGENT_ACTION_STATUS_FAILED) {
        throw new Error(action.error || response.resultSummary || 'Action execution failed')
      }

      if (action) {
        invalidateForEntityType(action.entityType, action.actionType)
      }

      toast.success(response.resultSummary || 'Action approved successfully')
      await fetchAgentFoundation()
    },
    [selectedOrganizationId, invalidateForEntityType, fetchAgentFoundation]
  )

  const rejectAction = useCallback(
    async (proposalId: string) => {
      if (!selectedOrganizationId) return

      const request = create(AgentRejectActionRequestSchema, {
        organizationId: selectedOrganizationId,
        proposalId,
      })

      const response = await agentClient.rejectAction(request)
      const action = response.action

      if (action) {
        setPendingActions((prev) => ({ ...prev, [action.id]: action }))
        setApprovals((prev) => prev.filter((proposal) => proposal.id !== action.id))
      }
      await fetchAgentFoundation()
    },
    [selectedOrganizationId, fetchAgentFoundation]
  )

  const runAgentStream = useCallback(async (
    userContent: string,
    streamFactory: (signal: AbortSignal) => AsyncIterable<AgentStreamEvent>,
  ): Promise<AgentStreamResult> => {
    if (isProcessing) return { succeeded: false, aborted: false }

    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    const assistantMessageId = createLocalMessageId('assistant')
    setMessages((prev) => [
      ...prev,
      { id: createLocalMessageId('user'), role: 'user', parts: [{ type: 'text', text: userContent }] },
      { id: assistantMessageId, role: 'assistant', parts: [], isStreaming: true },
    ])
    setIsProcessing(true)

    const updateAssistantMessage = (update: (message: Message) => Message) => {
      setMessages((prev) => prev.map((message) => (
        message.id === assistantMessageId ? update(message) : message
      )))
    }

    let hadStreamError = false
    try {
      for await (const event of streamFactory(controller.signal)) {
        if (event.turnId) {
          updateAssistantMessage((message) => (
            message.turnId === event.turnId ? message : { ...message, turnId: event.turnId }
          ))
        }
        switch (event.type) {
          case AgentEventType.TEXT_DELTA:
            if (event.data.case === 'textDelta') {
              const deltaText = event.data.value.text
              updateAssistantMessage((message) => {
                const parts = [...message.parts]
                const last = parts[parts.length - 1]
                if (last?.type === 'text') {
                  parts[parts.length - 1] = { type: 'text', text: last.text + deltaText }
                } else {
                  parts.push({ type: 'text', text: deltaText })
                }
                return { ...message, parts }
              })
            }
            break
          case AgentEventType.TOOL_USE_START:
            if (event.data.case === 'toolUseStart') {
              const { toolName, toolId } = event.data.value
              updateAssistantMessage((message) => ({
                ...message,
                parts: [...message.parts, { type: 'tool', tool: { toolName, toolId, status: 'running' as const } }],
              }))
            }
            break
          case AgentEventType.TOOL_EXECUTION:
            if (event.data.case === 'toolExecution') {
              const { toolName, toolId } = event.data.value
              updateAssistantMessage((message) => {
                if (message.parts.some((part) => part.type === 'tool' && part.tool.toolId === toolId)) return message
                return {
                  ...message,
                  parts: [...message.parts, { type: 'tool', tool: { toolName, toolId, status: 'running' as const } }],
                }
              })
            }
            break
          case AgentEventType.TOOL_RESULT:
            if (event.data.case === 'toolResult') {
              const { toolId, resultJson } = event.data.value
              updateAssistantMessage((message) => ({
                ...message,
                parts: message.parts.map((part) => part.type === 'tool' && part.tool.toolId === toolId
                  ? { ...part, tool: { ...part.tool, status: 'completed' as const, resultJson } }
                  : part),
              }))
            }
            break
          case AgentEventType.ACTION_PROPOSED:
            if (event.data.case === 'actionProposed') {
              const proposal = event.data.value
              setPendingActions((prev) => ({ ...prev, [proposal.id]: proposal }))
              if (proposal.status === AgentActionStatusEnum.AGENT_ACTION_STATUS_EXECUTED) {
                invalidateForEntityType(proposal.entityType, proposal.actionType)
              }
              if (proposal.status === AgentActionStatusEnum.AGENT_ACTION_STATUS_PROPOSED) {
                setApprovals((prev) => prev.some((item) => item.id === proposal.id) ? prev : [proposal, ...prev])
              }
              updateAssistantMessage((message) => {
                const existingIds = message.proposalIds ?? []
                return existingIds.includes(proposal.id)
                  ? message
                  : { ...message, proposalIds: [...existingIds, proposal.id] }
              })
            }
            break
          case AgentEventType.QUESTIONNAIRE_REQUESTED:
            if (event.data.case === 'questionnaireRequested') {
              const questionnaire = event.data.value
              setQuestionnaires((prev) => ({ ...prev, [questionnaire.id]: questionnaire }))
              updateAssistantMessage((message) => {
                const existingIds = message.questionnaireIds ?? []
                return existingIds.includes(questionnaire.id)
                  ? message
                  : { ...message, questionnaireIds: [...existingIds, questionnaire.id] }
              })
            }
            break
          case AgentEventType.PACK_LOADED:
            if (event.data.case === 'packLoaded') {
              const title = event.data.value.title || event.data.value.packId
              updateAssistantMessage((message) => ({
                ...message,
                parts: [...message.parts, { type: 'text', text: `Loaded the ${title} tools` }],
              }))
            }
            break
          case AgentEventType.COMPLETION:
            if (event.data.case === 'completion') {
              const completion = event.data.value
              setSessionId(completion.sessionId)
              const completedTurnId = completion.turnId || event.turnId
              if (completedTurnId) {
                updateAssistantMessage((message) => ({
                  ...message,
                  turnId: completedTurnId,
                }))
                void fetchDisclosure(completedTurnId)
              }
              if (completion.pendingActions.length > 0) {
                setPendingActions((prev) => {
                  const next = { ...prev }
                  for (const proposal of completion.pendingActions) next[proposal.id] = proposal
                  return next
                })
                setApprovals((prev) => {
                  const next = [...prev]
                  for (const proposal of completion.pendingActions) {
                    if (proposal.status === AgentActionStatusEnum.AGENT_ACTION_STATUS_PROPOSED && !next.some((item) => item.id === proposal.id)) {
                      next.unshift(proposal)
                    }
                  }
                  return next
                })
                updateAssistantMessage((message) => {
                  const mergedIds = [...(message.proposalIds ?? [])]
                  for (const proposal of completion.pendingActions) {
                    if (!mergedIds.includes(proposal.id)) mergedIds.push(proposal.id)
                  }
                  return { ...message, proposalIds: mergedIds }
                })
              }
              updateAssistantMessage((message) => ({ ...message, isStreaming: false }))
            }
            break
          case AgentEventType.ERROR:
            if (event.data.case === 'error') {
              hadStreamError = true
              updateAssistantMessage((message) => {
                const hasText = message.parts.some((part) => part.type === 'text' && part.text.length > 0)
                return {
                  ...message,
                  parts: hasText ? message.parts : [...message.parts, { type: 'text', text: 'Sorry, an error occurred. Please try again.' }],
                  isStreaming: false,
                }
              })
            }
            break
          case AgentEventType.HEARTBEAT:
            break
        }
      }
      updateAssistantMessage((message) => ({ ...message, isStreaming: false }))
      const succeeded = !hadStreamError
      // The user only "sees" a completed response if the dock panel or the full
      // chat page was open while it streamed — check the value at this instant,
      // not whatever it was when the stream started.
      if (succeeded && !isOpenRef.current && !isOnChatPageRef.current) setHasUnreadResponse(true)
      return { succeeded, aborted: false }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return { succeeded: false, aborted: true }
      }
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'ai-assistant-stream' })
      updateAssistantMessage((message) => message.isStreaming ? {
        ...message,
        parts: [{ type: 'text', text: 'Sorry, I encountered an error. Please try again.' }],
        isStreaming: false,
      } : message)
      return { succeeded: false, aborted: false }
    } finally {
      setIsProcessing(false)
      if (abortControllerRef.current === controller) abortControllerRef.current = null
    }
  }, [isProcessing, invalidateForEntityType, fetchDisclosure])

  const sendMessage = useCallback(async (content: string) => {
    const trimmed = content.trim()
    if (!selectedOrganizationId || !trimmed) return

    const requestId = crypto.randomUUID()
    const request = create(AgentSendMessageRequestSchema, {
      organizationId: selectedOrganizationId,
      message: trimmed,
      sessionId: sessionId ?? undefined,
      autopilot: autopilotMode,
      requestId,
      routeContext: create(AgentRouteContextSchema, {
        entityType: routeContext.entityType,
        entityId: routeContext.entityId,
        path: routeContext.path,
        timeZone: routeContext.timeZone,
      }),
    })
    await runAgentStream(trimmed, (signal) => resumableAgentStream(
      selectedOrganizationId,
      requestId,
      signal,
      () => agentClient.sendMessage(request, { signal }),
    ))
  }, [selectedOrganizationId, sessionId, autopilotMode, routeContext, runAgentStream])

  const submitQuestionnaire = useCallback(async (questionnaireId: string, answerInputs: QuestionnaireAnswerInput[]) => {
    if (!selectedOrganizationId) return
    const questionnaire = questionnaires[questionnaireId]
    if (!questionnaire || questionnaire.status !== AgentQuestionnaireStatusEnum.AGENT_QUESTIONNAIRE_STATUS_PENDING) return

    const answers: AgentQuestionnaireAnswer[] = answerInputs.map((answer) => create(AgentQuestionnaireAnswerSchema, {
      questionId: answer.questionId,
      values: answer.values,
    }))
    const requestId = crypto.randomUUID()
    const request = create(AgentSubmitQuestionnaireRequestSchema, {
      organizationId: selectedOrganizationId,
      questionnaireId,
      answers,
      autopilot: autopilotMode,
      requestId,
      routeContext: create(AgentRouteContextSchema, {
        entityType: routeContext.entityType,
        entityId: routeContext.entityId,
        path: routeContext.path,
        timeZone: routeContext.timeZone,
      }),
    })

    setQuestionnaires((prev) => ({
      ...prev,
      [questionnaireId]: {
        ...questionnaire,
        answers,
        status: AgentQuestionnaireStatusEnum.AGENT_QUESTIONNAIRE_STATUS_PROCESSING,
      },
    }))
    const streamResult = await runAgentStream(formatQuestionnaireResponse(questionnaire, answerInputs), (signal) => (
      resumableAgentStream(selectedOrganizationId, requestId, signal, () => {
        async function* events() {
          for await (const wrapped of agentClient.submitQuestionnaire(request, { signal })) {
            if (wrapped.event) yield wrapped.event
          }
        }
        return events()
      })
    ))
    if (streamResult.aborted) return

    setQuestionnaires((prev) => ({
      ...prev,
      [questionnaireId]: {
        ...(prev[questionnaireId] ?? questionnaire),
        answers: streamResult.succeeded ? answers : [],
        status: streamResult.succeeded
          ? AgentQuestionnaireStatusEnum.AGENT_QUESTIONNAIRE_STATUS_ANSWERED
          : AgentQuestionnaireStatusEnum.AGENT_QUESTIONNAIRE_STATUS_PENDING,
      },
    }))
    if (!streamResult.succeeded) {
      await loadSession(questionnaire.sessionId)
      toast.error('Could not submit those answers. Please try again.')
      return
    }
    await fetchAgentFoundation()
  }, [selectedOrganizationId, questionnaires, autopilotMode, routeContext, runAgentStream, loadSession, fetchAgentFoundation])

  // Handle cmd+I keyboard shortcut
  useEffect(() => {
    // Only enable keyboard shortcut if AI Agent feature is enabled
    if (!hasAIAgent) {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault()
        // If on chat page, go back; otherwise toggle sidebar
        if (isOnChatPage) {
          window.history.back()
        } else {
          toggle()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOnChatPage, hasAIAgent])

  // Fetch session list when sidebar opens
  useEffect(() => {
    if (isOpen && selectedOrganizationId) {
      fetchSessions()
    }
  }, [isOpen, selectedOrganizationId, fetchSessions])

  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])

  useEffect(() => {
    isOnChatPageRef.current = isOnChatPage
  }, [isOnChatPage])

  // Reopening the dock (or landing on the full chat page) means the user has
  // now seen whatever finished while it was closed.
  useEffect(() => {
    if (isOpen || isOnChatPage) setHasUnreadResponse(false)
  }, [isOpen, isOnChatPage])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const isWaitingForInput = Object.values(questionnaires).some((questionnaire) => (
    questionnaire.status === AgentQuestionnaireStatusEnum.AGENT_QUESTIONNAIRE_STATUS_PENDING
    || questionnaire.status === AgentQuestionnaireStatusEnum.AGENT_QUESTIONNAIRE_STATUS_PROCESSING
  ))

  return (
    <AIAssistantContext.Provider
      value={{
        isOpen,
        open,
        close,
        toggle,
        messages,
        sessionId,
        isProcessing,
        sendMessage,
        clearMessages,
        startNewSession,
        sessions,
        isLoadingSessions,
        fetchSessions,
        loadSession,
        isOnChatPage,
        setIsOnChatPage,
        pendingActions,
        approvals,
        attentionItems,
        attentionTotalCount,
        capabilities,
        capabilityContractVersion,
        isLoadingFoundation,
        fetchAgentFoundation,
        approveAction,
        rejectAction,
        autopilotMode,
        toggleAutopilot,
        questionnaires,
        submitQuestionnaire,
        isWaitingForInput,
        hasUnreadResponse,
        disclosures,
        fetchDisclosure,
        isLoadingDisclosure,
      }}
    >
      {children}
    </AIAssistantContext.Provider>
  )
}

export function useAIAssistant() {
  const context = useContext(AIAssistantContext)
  if (context === undefined) {
    throw new Error('useAIAssistant must be used within an AIAssistantProvider')
  }
  return context
}
